import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import * as HttpStatus from 'http-status-codes';
import { UserService } from '@sharedcommon/services/user.service';
import { environment } from '@sharedenvironment';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

  private readonly refreshLock: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private isRefreshing = false;

  constructor(private readonly userService: UserService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.manageHeaders(req);
    return next.handle(req).pipe(catchError((err) => {
      if(err.status == HttpStatus.StatusCodes.UNAUTHORIZED) {
        return this.handleUnauthorized(req, next);
      }
      return throwError(() => err);
    }));
  }

  private manageHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const headers: any = {};

    if (req.url.includes('/api/')) {
      // add X-activity user if user has activity
      if (this.userService.getActivity()) {
        headers['X-activity'] = this.userService.getActivity();
      }
      if (this.userService.getOperatingMode()) {
        headers['X-operating-mode'] = this.userService.getOperatingMode();
      }

      // note: bearer token is managed by MSAL HttpInterceptor
      // add api-key
      if (req.url.startsWith(environment.data.baseUrl)) {
        headers['Api-Key'] = environment.data.apiKey;
      } else if (req.url.startsWith(environment.identity.baseUrl)) {
        headers['Api-Key'] = environment.identity.apiKey;
      } else if (req.url.startsWith(environment.technical.baseUrl)) {
        headers['Api-Key'] = environment.technical.apiKey;
      } else if (req.url.startsWith(environment.equationManager.baseUrl)) {
        headers['Api-Key'] = environment.equationManager.apiKey;
      } else if (req.url.startsWith(environment.equation.baseUrl)) {
        headers['Api-Key'] = environment.equation.apiKey;
      }else if (req.url.startsWith(environment.sample.baseUrl)) {
        headers['Api-Key'] = environment.sample.apiKey;
      }else if (req.url.startsWith(environment.dms.baseUrl)) {
        headers['Api-Key'] = environment.dms.apiKey;
      } else if (req.url.startsWith(environment.organization.baseUrl)) {
        headers['Api-Key'] = environment.organization.apiKey;
      }
    }

    req = req.clone({
      setHeaders: headers
    });
    return req;
  }

  private handleUnauthorized(req: HttpRequest<any>, next: HttpHandler) {

    if (!this.isRefreshing) {
      // first 401, refreshing is marked and lock activated with null value
      this.isRefreshing = true;
      this.refreshLock.next(null);

      // call the refresh token service, it will be intercept by this interceptor
        this.isRefreshing = false;
        this.refreshLock.next(true);

        // Delay the response of 1 seconde... Azure token can be in the future
        return new Observable<HttpEvent<any>>(observer => {
          setTimeout(() => {
            next.handle(this.manageHeaders(req)).subscribe({
              next: (response) => {
                observer.next(response);
                observer.complete();
              },
              error: (error) => {
                observer.error(error);
                observer.complete();
              }
            });
          }, 1000);
        });

    } else {
      // store the incoming requests to be replayed when refresh token is recovered
      return this.refreshLock.pipe(filter(x => x != null),
        take(1),
        switchMap(() => {
          return next.handle(this.manageHeaders(req));
        }));
    }
  }
}
